import React, { useEffect, useState } from 'react';
import './SuggestedPromptsPage.scss';
import Title from '../../../shared/components/title/Title';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import useSuggestedQuestions from '../../../shared/hooks/suggested-prompts/UseSuggestedQuestions';
import Loader from '../../../shared/components/common/loader/Loader';
import { MonitorQuestion } from '../../../shared/models/monitor/MonitorQuestion';
import { PROJECTS } from '../../../shared/routes/path';
import { Navigate, useParams } from 'react-router-dom';
import { SuggestedPrompts } from '../../../shared/models/suggested-prompts/SuggestedQuestions';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';
import LimitTooltipComponent from '../../../shared/components/common/limit-tooltip/LimitTooltipComponent';
import useCanAddProject from '../../../shared/hooks/account/UseAddProject';
import { usePutMonitorsForProject } from '../../../shared/api/hooks/monitor/usePutMonitorsForProject';

const SuggestedPromptsPage: React.FC = () => {
  const { t } = useTranslation();
  const { accountResponse, projectResponse } = useAccount();
  const { projectId } = useParams();
  const projectIdNumber = Number(projectId);

  const [submitQuestions, setSubmitQuestions] = useState<string[]>([]);
  const [submittingSlug, setSubmittingSlug] = useState<string | null>(null);
  const [previousMonitorCount, setPreviousMonitorCount] = useState<number | null>(null);
  const [isWaitingForRefresh, setIsWaitingForRefresh] = useState(false);

  const { loading, suggestedPromptsResponse } = useSuggestedQuestions(projectIdNumber);
  const { canAddProject, refreshAccount } = useCanAddProject();
  const { addMonitorPrompts } = usePutMonitorsForProject(projectIdNumber);

  useEffect(() => {
    if (
      isWaitingForRefresh &&
      accountResponse &&
      previousMonitorCount !== null &&
      accountResponse.createdMonitorCount > previousMonitorCount
    ) {
      setIsWaitingForRefresh(false);
      setPreviousMonitorCount(null);
    }
  }, [accountResponse, previousMonitorCount, isWaitingForRefresh]);

  if (
    !isWaitingForRefresh &&
    (!accountResponse || !accountResponse.addons.includes('SUGGESTED_PROMPTS'))
  ) {
    return <Navigate to={PROJECTS} replace />;
  }

  if (!accountResponse) {
    return <></>;
  }

  const addQuestion = async (slug: string) => {
    if (!canAddProject) return;

    setSubmittingSlug(slug);

    if (!suggestedPromptsResponse || !projectIdNumber) {
      setSubmittingSlug(null);
      return;
    }

    const question = suggestedPromptsResponse.suggestedPrompts.find((q) => q.slug === slug);
    if (!question) {
      setSubmittingSlug(null);
      return;
    }

    const data = {
      prompt: question.question,
      status: 'PENDING',
    } as MonitorQuestion;

    try {
      await addMonitorPrompts({
        monitorQuestions: [data],
        projectId: projectIdNumber,
      });

      setPreviousMonitorCount(accountResponse.createdMonitorCount);
      setIsWaitingForRefresh(true);

      refreshAccount();

      setSubmitQuestions((prev) => [...prev, slug]);
    } catch (error) {
      console.error('Error adding question:', error);
      setIsWaitingForRefresh(false);
      setPreviousMonitorCount(null);
    } finally {
      setSubmittingSlug(null);
    }
  };

  if (loading || isWaitingForRefresh) {
    return <Loader />;
  }

  const searchVolumeBodyTemplate = (data: SuggestedPrompts) => {
    const isMoreThan100 = data.searchVolume > 100;
    return (
      <div className="search-volume">
        <div
          className={`search-volume__value search-volume__value${isMoreThan100 ? '--more' : '--less'}`}
        >
          {isMoreThan100 ? Intl.NumberFormat('pl-PL').format(data.searchVolume) : '<100'}
        </div>
        <div className="additional-panel">{data.phrase}</div>
      </div>
    );
  };

  const actionBodyTemplate = (slug: string, isAdded: boolean) => {
    const project = projectResponse?.find((project) => project.id === projectIdNumber);
    const isDemo = projectResponse && project?.demoVersion !== null && project?.demoVersion !== '';
    const isSubmitting = submittingSlug === slug;
    const isAnySubmitting = submittingSlug !== null;
    const isAlreadySubmitted = submitQuestions.includes(slug);
    const isDisabled = isAlreadySubmitted || isAnySubmitting || isAdded || !!isDemo || !canAddProject;

    return (
      <>
        {isSubmitting ? (
          <div className="table__cell-loader table__cell-loader--md" />
        ) : (
          <Button
            id={`btn-add-question-${slug}`}
            className={`action-btn ${!canAddProject ? 'btn--disabled-tooltip' : ''}`}
            onClick={() => addQuestion(slug)}
            disabled={isDisabled}
          >
            <span className={`ico--add ico--xs ${isDisabled && 'ico--disabled'}`} />
          </Button>
        )}
        {!isSubmitting &&
          (!canAddProject ? (
            <LimitTooltipComponent id={`btn-add-question-${slug}`} direction="top" />
          ) : (
            <CustomTooltip tooltipText={t('addToProject')} targetRef={`btn-add-question-${slug}`} />
          ))}
      </>
    );
  };

  return (
    <>
      <ConfirmDialog className="dialog-box" />
      <div className="suggested-prompt-page page-content">
        <div className="page-box">
          <Title
            title={t('suggestedPrompts')}
            childrenOnMain={<div className="title-box__subtitle">{t('discoverNewPrompts')}</div>}
          />
          <div className="page-box__stats">
            {suggestedPromptsResponse &&
              suggestedPromptsResponse.suggestedPrompts &&
              suggestedPromptsResponse.suggestedPrompts.length > 0 && (
                <div className={`table-content`}>
                  <DataTable
                    stripedRows
                    responsiveLayout="scroll"
                    removableSort
                    value={suggestedPromptsResponse.suggestedPrompts}
                    tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
                    tableClassName="table"
                    sortField={'searchVolume'}
                    sortOrder={-1}
                    className="table-wrapper"
                  >
                    <Column
                      sortable
                      field="prompt"
                      header={t('question')}
                      body={(data) => data.question || ''}
                      headerClassName="table__header"
                      style={{ minWidth: '120px', maxWidth: '100%' }}
                    />

                    <Column
                      sortable
                      field={'searchVolume'}
                      header={t('searchVolume')}
                      body={(data) => searchVolumeBodyTemplate(data)}
                      headerClassName="table__header table__header--center"
                      bodyClassName="table__cell--center"
                      style={{ width: '160px' }}
                    />
                    <Column
                      header={t('addToProject')}
                      body={(data) => actionBodyTemplate(data.slug, data.isAdded)}
                      bodyClassName="table__cell--gap table__cell--center"
                      headerClassName="table__header table__header--center"
                      align={'center'}
                      style={{ width: '34px' }}
                    />
                  </DataTable>
                </div>
              )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SuggestedPromptsPage;
